/* Iter122dv · Card "Logo e firma dello Studio" (branding assets).
   Estratto da pages/Impostazioni.js per riuso in pagina unificata "Impostazioni & Account".
   Iter122fn · Drag & drop: l'intera area preview accetta file rilasciati con il mouse. */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useDropZone } from "../lib/useDropZone";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Upload, Trash2, Image as ImageIcon, PenLine } from "lucide-react";

function AssetSlot({ kind, url, label, icon: Icon, testid, busy, backend, onUpload, onRemove }) {
  const { dragProps, isDragOver } = useDropZone((files) => onUpload(kind, files?.[0]), { disabled: busy });
  return (
    <div className="border-2 border-slate-200 rounded-lg p-4" data-testid={`branding-slot-${kind}`}>
      <div className="font-semibold text-slate-700 flex items-center gap-2 mb-2"><Icon className="w-4 h-4" /> {label}</div>
      <label
        {...dragProps}
        data-testid={`${kind}-dropzone`}
        className={`relative block h-32 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden mb-3 cursor-pointer transition-colors ${
          isDragOver
            ? "border-brand bg-[#FBEEF3]"
            : url
              ? "border-slate-300 bg-slate-50 hover:border-[#C98AA0]"
              : "border-slate-300 bg-slate-50 hover:border-[#C98AA0] hover:bg-[#FBEEF3]/40"
        } ${busy ? "opacity-70 pointer-events-none" : ""}`}
      >
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          disabled={busy} onChange={(e) => { onUpload(kind, e.target.files?.[0]); e.target.value = ""; }} />
        {url ? (
          <img src={`${backend}${url}?v=${Date.now()}`} alt={label} className="max-h-28 max-w-full object-contain" data-testid={`${kind}-preview`} />
        ) : (
          <div className="flex flex-col items-center justify-center text-center px-3 py-2 text-slate-500">
            <Upload className={`w-6 h-6 mb-1 ${isDragOver ? "text-brand" : "text-slate-400"}`} />
            <div className="text-sm font-semibold">{isDragOver ? "Rilascia per caricare" : `Trascina qui il ${kind === "logo" ? "logo" : "file firma"}`}</div>
            <div className="text-xs text-slate-400">oppure clicca per selezionarlo</div>
          </div>
        )}
        {isDragOver && url && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#FBEEF3]/85 text-brand font-bold text-sm border-2 border-brand rounded-lg">
            Rilascia per sostituire
          </div>
        )}
      </label>
      <div className="flex flex-wrap gap-2">
        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand hover:bg-brand-dark text-white text-sm font-semibold" data-testid={testid}>
          <Upload className="w-4 h-4" /> {busy ? "Caricamento…" : (url ? "Sostituisci" : "Carica")}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            disabled={busy} onChange={(e) => { onUpload(kind, e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {url && (
          <Button variant="outline" size="sm" onClick={() => onRemove(kind)} disabled={busy}
            className="h-9 text-red-600 border-red-300 hover:bg-red-50" data-testid={`${kind}-remove`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function LogoFirmaCard({ onChange }) {
  const [assets, setAssets] = useState(null);
  const [busy, setBusy] = useState({ logo: false, firma: false });
  const backend = process.env.REACT_APP_BACKEND_URL;
  // Contatore versione: incrementato ad ogni load() per permettere al parent di
  // distinguere upload/sostituzione/rimozione anche quando l'URL non cambia.
  const versionRef = useRef(0);
  const load = () => api.get("/me/tenant/branding-assets").then((r) => {
    versionRef.current += 1;
    const next = { ...r.data, __v: versionRef.current };
    setAssets(next);
    if (typeof onChange === "function") onChange(next);
  });
  useEffect(() => { load(); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("File troppo grande (max 2 MB)"); return; }
    setBusy((b) => ({ ...b, [kind]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/me/tenant/branding/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(kind === "logo" ? "Logo caricato" : "Firma caricata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito");
    }
    setBusy((b) => ({ ...b, [kind]: false }));
  };

  const remove = async (kind) => {
    if (!window.confirm(`Rimuovere ${kind === "logo" ? "il logo" : "la firma grafica"}?`)) return;
    setBusy((b) => ({ ...b, [kind]: true }));
    try {
      await api.delete(`/me/tenant/branding/${kind}`);
      toast.success("Rimosso");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rimozione non riuscita");
    }
    setBusy((b) => ({ ...b, [kind]: false }));
  };

  return (
    <Card className="p-5 sm:p-6" data-testid="branding-assets-card">
      <div className="mb-4">
        <h2 className="font-bold text-xl text-slate-800">Logo e firma dello Studio</h2>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          Il logo compare in intestazione dei PDF (preventivi, sinistri, Credius) e negli avvisi. La firma grafica appare in calce ai documenti e nell&apos;editor di preventivi/sinistri.
          Se non è impostata alcuna firma, verrà usata la <b>ragione sociale</b> del profilo come firma testuale{assets?.fallback_firma_testo ? <> (attualmente <span className="font-mono text-slate-800">&laquo;{assets.fallback_firma_testo}&raquo;</span>)</> : ""}.
        </p>
        <p className="text-xs text-slate-500 mt-1">Formati accettati: PNG, JPG, WEBP · dimensione massima 2 MB · sfondo trasparente consigliato. Puoi anche trascinare il file direttamente nell&apos;area di anteprima.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AssetSlot kind="logo" url={assets?.logo_url} label="Logo dello Studio" icon={ImageIcon} testid="upload-logo-btn" busy={busy.logo} backend={backend} onUpload={upload} onRemove={remove} />
        <AssetSlot kind="firma" url={assets?.firma_url} label="Firma grafica" icon={PenLine} testid="upload-firma-btn" busy={busy.firma} backend={backend} onUpload={upload} onRemove={remove} />
      </div>
    </Card>
  );
}
